import React, { useEffect, useRef, useState } from "react";
import "./style.css";
import CustomCard from "../../../components/d-reusable/CustomCard";
import CardHeaderWithBottomLine from "../../../components/d-reusable/CardHeaderWithBottomLine";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Radio,
  Row,
  Switch,
  Table,
} from "antd";
import TextArea from "antd/lib/input/TextArea";
import CustomPrimaryButton from "../../../components/d-reusable/CustomPrimaryButton";
import CustomDefaultButton from "../../../components/d-reusable/CustomDefaultButton";
import CustomButtonWithIcon from "../../../components/d-reusable/CustomButtonWithIcon";
import { dentalIcons } from "../../../utils-icons/dentalIcons";
import CustomDisabledScreen from "../../../components/d-reusable/CustomDisabledScreen";
import CustomDrawer from "../../../components/d-reusable/CustomDrawer";
import AddCollaborator from "../add-collaborator/AddCollaborator";
import { dummyCollaborators } from "../../../d-helper";
import { useLocation, useNavigate } from "react-router-dom";
import { dentalBasePath, errorRequestHandel } from "../../../../helper";
import { createDentalIndentificationProject } from "../../../../components/https/projects-apis";
import { getMyOrganization } from "../../../../components/https/planSubscriptions";
import { useSelector } from "react-redux";
import { useSubscriptionGuard } from "../../../../hooks/subscription-hook/UseSubscriptionGuard";

const CreateNewProject = () => {
  useSubscriptionGuard();
  const navigate = useNavigate();
  const [projectForm] = Form.useForm();
  const errorRef = useRef(null);

  const userdata = useSelector(
    (state) => state.settings_data.profileSettings_data
  );

  const location = useLocation();
  const { record } = location.state || {}; // Destructure state values

  const [open, setOpen] = useState(false);
  const [isOpenSearch, setIsOpenSearch] = useState("closed");
  const [canCollaboratorAdded, setCanCollaboratorAdded] = useState("private");
  const [loading, setLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (record) {
      // Populate the form with the record data
      projectForm.setFieldsValue({
        identifier: record.identifier,
        title: record.title,
        description: record.description,
        total_am_cases: record.total_am_cases,
        total_pm_cases: record.total_pm_cases,
        is_open: record.is_open ? 1 : 0,
        incident_type: record.incident_type,
      });
      setIsOpenSearch(
        record?.incident_type === "open_search" ? "open" : "closed"
      );
      setCollaborators(
        record?.collaborators?.filter((col) => col.examiner_type !== "primary")
      );
      setCanCollaboratorAdded(record.is_open ? "open" : "private");
    }
  }, [record, projectForm]);

  useEffect(() => {
    getMyOrganizations();
  }, []);

  const getMyOrganizations = async () => {
    try {
      setLoading(true);
      const resposen = await getMyOrganization();
      if (resposen.status === 200) {
        const updatedExaminers = resposen.data.data.examiners?.filter(
          (examiner) => examiner.id !== userdata?.id
        );
        setUsers(updatedExaminers);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      errorRequestHandel({ error });
    }
  };

  const addCollaborator = (item) => {
    const newItem = {
      ...item,
      canManageAmCases: false,
      canManagePmCases: false,
    };
    setCollaborators((prevCollaborators) => [...prevCollaborators, newItem]);
  };

  const remvoeCollaborator = (item) => {
    setCollaborators((prev) => {
      // Return the filtered array after removing the collaborator with the given id
      return prev.filter((el) => el.id !== item.id);
    });
  };

  const updateCollaboratorAccess = (id, key, value) => {
    setCollaborators((prev) =>
      prev.map((collab) =>
        collab.id === id ? { ...collab, [key]: value } : collab
      )
    );
  };
  const onFinishFailed = (errorInfo) => {
    const errorField = errorInfo.errorFields[0]; // Get the first error field
    if (errorField.name[0] === "collaborator") {
      return;
    }
    if (errorField) {
      errorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const onRegister = async (values) => {
    try {
      const payload = {
        ...values,
        total_am_cases:
          values?.incident_type === "open_search"
            ? 0
            : Number(values?.total_am_cases),
        total_pm_cases:
          values?.incident_type === "open_search"
            ? 0
            : Number(values?.total_pm_cases),
        collaborators:
          values?.is_open === 1
            ? []
            : collaborators
                .filter((col) => col.examiner_type !== "primary")
                .map(({ id, canManageAmCases, canManagePmCases }) => ({
                  id,
                  canManageAmCases,
                  canManagePmCases,
                })),
        ...(record ? { id: record.id } : {}), // Add id key if record exists
      };

      setLoading(true);

      const response = await createDentalIndentificationProject({
        payload: payload,
      });

      if (response.status === 200) {
        message.success("Project has been created successfully");
        navigate(`${dentalBasePath}/projects`);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      errorRequestHandel({ error });
    }
  };

  return (
    <div className="d-create-new_project_container" ref={errorRef}>
      <CustomDisabledScreen loading={loading} />
      <Form
        layout="vertical"
        className="basic_antForm"
        form={projectForm}
        onFinish={onRegister}
        onFinishFailed={onFinishFailed}
        initialValues={{ is_open: 0, incident_type: "closed_search" }}
      >
        <CustomCard>
          <div className="create-new-project_form_container">
            <CardHeaderWithBottomLine title={"Add Basic Details"} />
            <br />

            <Row gutter={[10, 10]}>
              <Col md={4} xs={24}>
                <p className="form_basic_label">Project Title</p>
              </Col>
              <Col md={12} xs={24}>
                <Form.Item
                  name="title"
                  rules={[
                    { required: true, message: "This field is required" }, // Validation rules
                  ]}
                >
                  <Input placeholder="Project Title" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 10]}>
              <Col md={4} xs={24}>
                <p className="form_basic_label">Project Description </p>
              </Col>
              <Col md={12} xs={24}>
                <Form.Item name={"description"}>
                  <TextArea
                    className="form_basic_text_area"
                    placeholder="Project Description"
                    cols={4}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[10, 10]}>
              <Col md={4}>
                <p className="form_basic_label">Incident Type</p>
              </Col>
              <Col md={12}>
                <Form.Item
                  name="incident_type"
                  rules={[
                    {
                      required: true,
                      message: "Please select a project collaborator", // Validation message
                    },
                  ]}
                >
                  <Radio.Group name="colloborator">
                    <Row gutter={[16, 20]}>
                      <Col md={12}>
                        <Radio
                          value={"closed_search"}
                          onChange={() => setIsOpenSearch("closed")}
                        >
                          Closed Search
                          <div
                            style={{
                              color: "#5F5F5F",
                              fontSize: "14px",
                              fontFamily: "Poppins-Regular",
                              width: 200,
                            }}
                          >
                            Identified List
                          </div>
                        </Radio>
                      </Col>
                      <Col md={12}>
                        <Radio
                          value={"open_search"}
                          onChange={() => setIsOpenSearch("open")}
                        >
                          Open Search
                          <div
                            style={{
                              color: "#5F5F5F",
                              fontSize: "14px",
                              fontFamily: "Poppins-Regular",
                            }}
                          >
                            Unidentified List
                          </div>
                        </Radio>
                      </Col>
                    </Row>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            {isOpenSearch === "closed" && (
              <>
                <Row gutter={[20, 40]}>
                  <Col md={4}>
                    <p className="form_basic_label">AM Cases</p>
                  </Col>
                  <Col md={12}>
                    <Form.Item
                      onWheel={(e) => e.target.blur()}
                      name="total_am_cases"
                      rules={[
                        { required: true, message: "This field is required" }, // Validation rules
                      ]}
                    >
                      <Input type="number" min={1} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[20, 40]}>
                  <Col md={4}>
                    <p className="form_basic_label">PM Cases</p>
                  </Col>
                  <Col md={12}>
                    <Form.Item
                      onWheel={(e) => e.target.blur()}
                      name="total_pm_cases"
                      rules={[
                        { required: true, message: "This field is required" }, // Validation rules
                      ]}
                    >
                      <Input type="number" min={1} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </div>
        </CustomCard>
        <br />
        <CustomCard>
          <div className="create-new-project_form_container">
            <CardHeaderWithBottomLine title={"Collaboration Details"} />
            <br />
            <p
              style={{
                fontSize: 14,
                fontFamily: "Poppins-Regular",
                paddingBottom: 10,
              }}
            >
              Select Type of collaboration
            </p>
            <Form.Item
              name="is_open"
              rules={[
                {
                  required: true,
                  message: "Please select a project collaborator", // Validation message
                },
              ]}
            >
              <Radio.Group name="colloborator">
                <Row gutter={[16, 20]}>
                  <Col md={24}>
                    <Radio
                      value={0}
                      onChange={() => setCanCollaboratorAdded("private")}
                    >
                      Restricted Collaboration
                      <div
                        style={{
                          color: "#5F5F5F",
                          fontSize: "14px",
                          fontFamily: "Poppins-Regular",
                        }}
                      >
                        Only Selected Examiners can add records
                      </div>
                    </Radio>
                  </Col>
                  <Col md={24}>
                    <Radio
                      value={1}
                      onChange={() => setCanCollaboratorAdded("public")}
                    >
                      Open Collaboration
                      <div
                        style={{
                          color: "#5F5F5F",
                          fontSize: "14px",
                          fontFamily: "Poppins-Regular",
                        }}
                      >
                        Anybody from the organization can add records
                      </div>
                    </Radio>
                  </Col>
                </Row>
              </Radio.Group>
            </Form.Item>
            <br />
            <br />
            {collaborators.length > 0 && canCollaboratorAdded !== "public" && (
              <Table
                dataSource={collaborators}
                columns={[
                  {
                    title: "Collaborator Name",
                    key: "first_name",
                    render: (text, record) => (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          fontSize: 14,
                        }}
                      >
                        <span>
                          {record?.first_name
                            ? record?.first_name
                            : record?.name}
                        </span>
                        <small>
                          {record?.email ? record?.email : record?.user_name}
                        </small>
                      </div>
                    ),
                  },

                  {
                    title: "Manage AM Cases",
                    dataIndex: "canManageAmCases",
                    key: "canManageAmCases",
                    render: (text, record) => (
                      <Switch
                        style={{
                          backgroundColor: record.canManageAmCases
                            ? "#20C3FF"
                            : undefined,
                        }}
                        checked={record.canManageAmCases}
                        onChange={(checked) => {
                          updateCollaboratorAccess(
                            record.id,
                            "canManageAmCases",
                            checked
                          );
                        }}
                      />
                    ),
                  },
                  {
                    title: "Manage PM Cases",
                    dataIndex: "canManagePmCases",
                    key: "canManagePmCases",
                    render: (text, record) => (
                      <Switch
                        style={{
                          backgroundColor: record.canManagePmCases
                            ? "#20C3FF"
                            : undefined,
                        }}
                        checked={record.canManagePmCases}
                        onChange={(checked) => {
                          updateCollaboratorAccess(
                            record.id,
                            "canManagePmCases",
                            checked
                          );
                        }}
                      />
                    ),
                  },
                  {
                    title: "Action",
                    dataIndex: "action",
                    key: "action",
                    render: (text, record) => {
                      // Ensure correct destructuring: text, record
                      return (
                        <div
                          style={{
                            color: "#20C3FF",
                            fontSize: 14,
                            fontFamily: "Poppins-Regular",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            remvoeCollaborator(record); // Use record's id to delete
                          }}
                        >
                          <img src={dentalIcons.binIcon} alt="Delete" />
                        </div>
                      );
                    },
                  },
                ]}
                pagination={false}
              />
            )}
            <br />
            {canCollaboratorAdded === "private" && (
              <CustomButtonWithIcon
                title="Add Collaborator"
                icon={dentalIcons.userAddicon}
                onClick={() => setOpen(true)}
              />
            )}
            <br />
          </div>
        </CustomCard>
        <br />
        <Row gutter={[16, 40]}>
          <Col md={4}>
            <CustomDefaultButton title="Cancel" onClick={() => navigate(-1)} />
          </Col>
          <Col md={4}>
            <CustomPrimaryButton
              title="Register"
              type={"submit"}
              isLoading={loading}
            />
          </Col>
        </Row>
      </Form>

      <CustomDrawer
        open={open}
        onClose={() => setOpen(false)}
        title="Add Collaborators"
      >
        <AddCollaborator
          addCollaborator={(item) => addCollaborator(item)}
          remvoeCollaborator={(item) => remvoeCollaborator(item)}
          examinarsData={collaborators}
          isLoading={false}
          loadingItemId={null}
          users={users}
        />
      </CustomDrawer>
    </div>
  );
};

export default CreateNewProject;
